export const NEEDS_ATTENTION_LIMIT = 3

/**
 * Transactions a descriptor needs before its ratio means anything.
 *
 * Below `100 / limit` transactions a single notice already exceeds the VAMP limit,
 * so the ratio reports "one thing happened here" rather than a real trend — a
 * descriptor with 2 notices on 1 transaction reads as 200%. Such descriptors keep
 * their true ratio but rank below every statistically meaningful one.
 */
const minSignificantTransactions = (limit) => (limit > 0 ? Math.ceil(100 / limit) : 0)

const significanceTest = (limit) => {
  const threshold = minSignificantTransactions(limit)
  return (d) => d.transactions >= threshold
}

// Equal ratios are common (a single notice on a handful of transactions reads as
// 100%), so transaction volume decides which of them the merchant sees first.
const byRatioThenVolume = (a, b) =>
  b.ratio - a.ratio || b.transactions - a.transactions || a.name.localeCompare(b.name)

/**
 * Splits descriptors into the "Needs attention" block and the rest of the list.
 *
 * @param {{ name: string, ratio: number, transactions: number, cases: number }[]} descriptors
 * @param {number} limit  regional VAMP limit, % (0 / NaN when the region is unknown)
 * @param {string} query  trimmed, lower-cased search text
 * @returns {{ attention: object[], rest: object[] }}
 */
export const rankDescriptors = (descriptors, limit, query) => {
  const isSignificant = significanceTest(limit)

  const found = descriptors
    .filter((d) => !query || d.name.toLowerCase().includes(query))
    .sort((a, b) => Number(isSignificant(b)) - Number(isSignificant(a)) || byRatioThenVolume(a, b))

  const riskScore = (d) => (d.ratio / limit) * d.cases
  const attention =
    query || !limit
      ? []
      : found
          .filter((d) => isSignificant(d) && d.ratio >= limit / 2)
          .sort((a, b) => riskScore(b) - riskScore(a))
          .slice(0, NEEDS_ATTENTION_LIMIT)

  return { attention, rest: found.filter((d) => !attention.includes(d)) }
}
